import type { PopoverInterface, PopoverOptions } from "flowbite";
import { Popover } from "flowbite";
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus";
import { generateUID } from "@/lib/helpers";
import { trackMobileWebEvent } from "@/lib/pendo";
import provideHook from "@/lib/provideHook";
import { withStartParam } from "@/lib/scheduleStartParam";
import { getQueryVariables, updateQueryParam } from "@/lib/url";

import AdminTimeClockChannel from "@/channels/AdminTimeClockChannel.js";
import { jobShowPath, userShowPath } from "@/mixins/path_helpers";
import EventCalendar, { CalendarLayout } from "@/mobiscroll/EventCalendar";
import {
  expandMultiTechRow,
  isUnassignedAppointment
} from "@/SchedulerV2/appointmentCounts";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import { AdminPreference } from "@/typings/adminPreferences";
import { TimelineEvent } from "@/typings/scheduler";
import {
  EventAPIResponse,
  ResourceAPIResponse,
  UNASSIGNED_RESOURCE_ID
} from "@/typings/scheduler";
import {
  MbscCellClickEvent,
  MbscEventcalendarOptions,
  MbscEventClickEvent
} from "@mobiscroll/javascript";
import { getUnavailableColorsFromBusinessHours } from "./getUnavailableColorsForResource";
import {
  loadSavedTechnicianIds,
  readTechnicianIdsParam,
  saveTechnicianIds,
  writeTechnicianIdsParam
} from "./technicianSelectionPersistence";

// Interface for using EventCalendar
export const useEventCalendar = provideHook(() => {
  const store = useStore();
  const { getStatusCustomizationName } = useScheduleColors();
  const calendar = ref<EventCalendar | null>(null);
  // Set by whichever timeline mounts the calendar; the phone layout keeps one
  // active resource where the desktop layout shows them all.
  const layout = ref<CalendarLayout>("desktop");

  /**
   * Whether the phone board draws the applied selection as side-by-side
   * columns rather than the one active schedule.
   *
   * Read straight off the tenant rather than through useMobileWebOptimization:
   * everything getComponentHtml mounts — resource cells, column headers, event
   * cards — gets its own Vue app with no inject context, so a
   * `useEventCalendar()` call inside one rebuilds this whole hook per cell per
   * repaint. Those components all read the store directly to avoid that, and
   * useMobileWebOptimization would reintroduce the cost here by bringing
   * useBreakpoints, which registers matchMedia and resize listeners. `layout`
   * already carries the viewport answer here: it is only ever "mobile" when
   * MobileTimeline mounted the calendar, which SchedulePageV2 gates on the
   * same breakpoint.
   *
   * A missing key and an explicit false are the same answer -- the jbuilder
   * omits a disabled flag entirely -- so an unanswered tenant reads as off and
   * the board stays on its single schedule until the answer lands.
   */
  const mobileWebOptimization = computed(() =>
    Boolean(store.state.tenant?.feature_flags?.mobile_web_optimization)
  );

  // The key is absent when the flag is off, so an unanswered tenant reads as off.
  const multiTechEnabled = computed(() =>
    Boolean(store.state.tenant?.feature_flags?.multiple_technicians_ff_enabled)
  );

  /**
   * The flag AND the phone calendar actually being the one mounted. Used where
   * the layout is the question — which resources exist, which surface owns the
   * unassigned work — rather than what a selection would draw.
   */
  const phoneColumnsEnabled = computed(
    () => layout.value === "mobile" && mobileWebOptimization.value
  );

  /**
   * Which schedules the admin last applied in the technician sheet. Held here
   * rather than on the page because `setResources` is what pushes columns at
   * the calendar, and it cannot read page state.
   */
  const selectedResourceIds = ref<number[]>([]);

  /**
   * Whether the saved or linked selection has had its one chance to apply.
   * Once it has — or once the admin applies a selection of their own — a later
   * roster change must not put the stored one back over what is on screen.
   */
  let selectionRestored = false;

  const activeResource = ref<ResourceAPIResponse | null>(null);
  const eventPoll = ref<ReturnType<typeof setInterval> | null>(null);
  const selectedAppointmentId = ref<string | number | undefined | null>(null);
  const channel = ref<AdminTimeClockChannel | null>(null);
  const selectedEventId = ref<string | number | undefined | null>(null);
  const isWeekView = computed<boolean>(
    () => store.getters.scheduleWeekViewActive
  );

  const isVisible = (event: EventAPIResponse) => {
    const { jobStatusIds, departmentIds, serviceCategoryIds, statuses } =
      filters.value;

    if (jobStatusIds.length) {
      if (!jobStatusIds.includes(event.appointment?.job_status?.key || ""))
        return false;
    }

    if (departmentIds.length && event.technician) {
      if (!departmentIds.includes(event.technician?.department.id))
        return false;
    }

    if (serviceCategoryIds.length) {
      if (!serviceCategoryIds.includes(event.appointment?.service_category_id))
        return false;
    }

    if (statuses.length) {
      const status = getStatusCustomizationName(event.appointment);
      if (!statuses.includes(status)) return false;
    }

    return true;
  };
  const filters = computed(() => store.state.timeline.filters);
  const events = computed<EventAPIResponse[]>(() => {
    const allEvents: EventAPIResponse[] = [
      ...store.state.timeline.events
    ].reduce((_events: EventAPIResponse[], event: EventAPIResponse) => {
      if (event.appointment?.ghost) {
        _events.push({
          ...event,
          appointment: {
            ...event.appointment,
            ghost: false,
            unassigned: false
          },
          technician: event.ghost_technician,
          is_ghost: true,
          id: generateUID()
        });
      }
      _events.push(
        ...expandMultiTechRow(event, {
          enabled: multiTechEnabled.value,
          // Read only when on, so flag-off boards don't redraw on a resource load.
          resources: multiTechEnabled.value
            ? (store.state.timeline.resources ?? [])
            : [],
          now: store.state.timeline.events_fetched_at
        })
      );
      return _events;
    }, []);
    return allEvents.filter((event) => isVisible(event));
  });
  const unassignedRowEnabled = computed(() => {
    // The phone board always carries an Unassigned column. The preference
    // exists to declutter a wide desktop board, and moving that work into a
    // modal is the thing this story removes — but only where the columns
    // render, so a flag-off phone keeps the preference's current meaning.
    if (phoneColumnsEnabled.value) return true;

    const preference: AdminPreference | undefined =
      store.state.adminPreferences.find(
        (preference: AdminPreference) =>
          preference.key === "sheduler_unassigned_events_floating"
      );
    return !preference?.enabled;
  });
  const resources = computed<
    | ResourceAPIResponse[]
    | { id: number; full_name: string; avatar: any; department: any }[]
  >(() => {
    const resources:
      | ResourceAPIResponse
      | { id: number; full_name: string; avatar: any; department: any }[] = [];
    if (unassignedRowEnabled.value) {
      resources.push({
        id: UNASSIGNED_RESOURCE_ID,
        full_name: "Unassigned",
        avatar: {},
        department: {}
      });
    }
    const filteredTechs = filters.value?.departmentIds.length
      ? store.state.timeline.resources.filter((tech: ResourceAPIResponse) => {
          return filters.value.departmentIds.some((deptId) =>
            tech.belongs_to_departments.includes(deptId)
          );
        })
      : store.state.timeline.resources;
    return resources.concat(
      filteredTechs
        .slice()
        .sort((a: ResourceAPIResponse, b: ResourceAPIResponse) =>
          a.full_name < b.full_name ? -1 : 1
        )
    );
  });
  const unassignedEvents = computed(() => {
    if (!calendar.value) return [];
    return events.value.filter(isUnassignedAppointment).map((event) => {
      return new TimelineEvent({
        id: event.id,
        title: calendar.value?.getEventTitle(event) || "",
        start: calendar.value?.getEventStart(event) || "",
        end: calendar.value?.getEventEnd(event) || "",
        resource: event.technician?.id || UNASSIGNED_RESOURCE_ID,
        ghost: Boolean(event.appointment.ghost),
        editable: event.appointment.status === "pending",
        details: event
      });
    });
  });
  watch(
    events,
    (val, oldVal) => {
      if (val !== oldVal) {
        calendar.value?.setEvents(val);
      }
    },
    { deep: true }
  );
  watch(resources, (val, oldVal) => {
    if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
      const grew = val.length > oldVal.length && layout.value === "mobile";
      // A restore draws its own columns; drawing the default first would lay
      // the grid out twice on a cold load.
      if (grew && restoreSelection()) return;

      setResources(val);
      // applyTechnicianOffHourHighlights(val);
      // Apply active resource query param on mobile only
      if (grew) {
        const params = getQueryVariables();
        if (params.resource) {
          activeResource.value = resources.value.find(
            (resource: ResourceAPIResponse) =>
              resource.id.toString() === params.resource
          );
          if (activeResource.value) {
            setActiveResource(activeResource.value);
          }
        }
      }
    }
  });

  // Week view asks for the whole ISO week the date falls in. Monday-first
  // matches the desktop board, which sets `firstDay: 1` deliberately, and the
  // range the date row's title already names.
  const getDateFilters = (date) => {
    const unit = isWeekView.value ? "isoWeek" : "day";
    return {
      start_date: moment(date).startOf(unit),
      end_date: moment(date).endOf(unit)
    };
  };

  /** The applied selection, minus anything no longer on the roster. */
  const selectedResources = computed(() =>
    resources.value.filter((resource) =>
      selectedResourceIds.value.includes(resource.id)
    )
  );

  /**
   * Unassigned alone is saved as nothing, because it is what the phone board
   * shows by default. That is also how "Clear" is committed: the sheet keeps
   * Unassigned checked, so Show hands over exactly `[Unassigned]`.
   */
  const persistSelection = (ids: number[]) => {
    if (!phoneColumnsEnabled.value) return;

    const isDefault = ids.length === 1 && ids[0] === UNASSIGNED_RESOURCE_ID;
    const kept = isDefault ? [] : ids;
    writeTechnicianIdsParam(kept, { replace: true });
    saveTechnicianIds(kept);
  };

  /**
   * Draws a selection and keeps the address in step with it. A single pick goes
   * through `setActiveResource`, which writes `?resource=`, so it deep-links as
   * the single-select picker did. Any other selection names no one schedule and
   * clears the param: left behind, it would restore the board to whichever
   * schedule was active last.
   *
   * Every write is a replace. Choosing columns is not a navigation, and the
   * board does not follow Back: a pushed entry would leave Back on an address
   * the board no longer shows, and a reload there would bring back — and save
   * over the current one — the older selection.
   */
  const commitSelection = (ids: number[]) => {
    selectedResourceIds.value = ids;
    selectionRestored = true;

    const single =
      ids.length === 1
        ? resources.value.find((resource) => resource.id === ids[0])
        : undefined;
    if (single) {
      setActiveResource(single as ResourceAPIResponse, { replace: true });
    } else {
      setResources(resources.value as ResourceAPIResponse[]);
      if (ids.length !== 1) updateQueryParam("resource", null, true);
    }
    persistSelection(ids);
  };

  const setSelectedResources = (ids: number[]) => commitSelection(ids);

  /**
   * Puts back the selection a link or an earlier visit left, once per board.
   * The address wins over the browser; a `?resource=` link with no technician
   * list is left to its own branch in the roster watch, so it still opens that
   * one schedule.
   *
   * Validated against the whole roster rather than the department-filtered
   * `resources`: a department chip hiding a technician is not the technician
   * leaving the board, and pruning on it would lose them from the saved copy.
   *
   * Called from the roster watch (a cold load) and from `initCalendar` (an
   * in-app return, where the store already holds the roster and the watch never
   * fires, and a desktop board turned to phone). It waits, without spending its
   * one chance, until the phone board is on screen and the roster is known.
   */
  const restoreSelection = (): boolean => {
    const roster: ResourceAPIResponse[] = store.state.timeline?.resources || [];
    if (selectionRestored || !phoneColumnsEnabled.value || !roster.length) {
      return false;
    }
    selectionRestored = true;

    const linked = readTechnicianIdsParam();
    const params = getQueryVariables() as { resource?: string };
    if (linked === null && params.resource) return false;

    const wanted = linked ?? loadSavedTechnicianIds();
    if (!wanted) return false;

    const onBoard = new Set([
      UNASSIGNED_RESOURCE_ID,
      ...roster.map((resource) => resource.id)
    ]);
    const valid = wanted.filter((id) => onBoard.has(id));

    // Unassigned alone is the default board, and committing it would clear
    // the saved selection — so it counts as naming nobody here.
    if (!valid.some((id) => id !== UNASSIGNED_RESOURCE_ID)) {
      // A link that names nobody still on the board shows the default board
      // but leaves the saved selection alone: a bad link is not the admin
      // clearing theirs. A saved selection with nobody left is simply spent.
      if (linked) writeTechnicianIdsParam([], { replace: true });
      else saveTechnicianIds([]);
      return false;
    }

    commitSelection(valid);
    return true;
  };

  /**
   * What the phone grid draws — the single source for both the columns pushed
   * at the calendar and the count reported anywhere else.
   *
   * Falls back to the board's own schedule whenever the selection is empty or
   * a roster change emptied it: rendering zero columns reads as a broken board
   * rather than an unfiltered one.
   *
   * Uncapped: every schedule picked is drawn, however narrow that makes it.
   * Mobiscroll's schedule view squashes columns rather than scrolling them, so
   * a wide selection is its own feedback -- the cards shrink where the admin
   * can see them shrink, and one deselect undoes it.
   */
  const phoneColumns = computed(() => {
    const fallback = activeResource.value ? [activeResource.value] : [];
    // Deliberately NOT gated on `layout`: the page's chrome reads this before
    // any calendar is mounted, and the only consumer that cares about the
    // layout is setResources, which is already inside its mobile branch.
    if (!mobileWebOptimization.value) return fallback;

    return selectedResources.value.length ? selectedResources.value : fallback;
  });

  // The inverse of initCalendar: the page keeps this hook alive across the
  // phone/desktop swap, so anything init acquires has to be released here or
  // it accumulates once per rotation across the md breakpoint.
  const destroyCalendar = () => {
    calendar.value?.destroy();
    calendar.value = null;

    stopEventPolling();
    channel.value?.disconnect();
    channel.value = null;
    window.handleSchedulerEventEdit = null;
    EventBus.$off("refresh-schedule-board", refreshTimeline);
  };

  const fetchTimelineData = (filters: { start_date: Date; end_date: Date }) => {
    return store.dispatch("fetchTimelineEvents", {
      start_date: filters.start_date.toISOString(),
      end_date: filters.end_date.toISOString()
    });
  };

  const fetchResources = () => {
    store.dispatch("fetchTimelineResources");
  };

  const initCalendar = (
    node: HTMLElement | null,
    date: Date,
    options?: MbscEventcalendarOptions,
    calendarLayout: CalendarLayout = "desktop",
    // The mobile_web_optimization flag. Opt-in only, default false: the
    // desktop mount (Timeline.vue) never passes this argument, and the phone
    // mount passes false while the flag is off -- both reach EventCalendar
    // with the same false this parameter defaults to.
    mobileWebOptimization = false
  ) => {
    if (node) {
      layout.value = calendarLayout;
      const zoomLevel = localStorage.getItem("scheduler-zoom") || 50;
      calendar.value = new EventCalendar({
        node,
        options: { ...options, selectedDate: date, zoomLevel },
        layout: calendarLayout,
        mobileWebOptimization
      });
      applyScheduleView(date);
      loadData(getDateFilters(date));

      startEventPolling();
      connectToChannel();
      // Register edit event click
      window.handleSchedulerEventEdit = (appointmentId) => {
        selectedAppointmentId.value = appointmentId;
        if (window.closeUnassignedModal) {
          window.closeUnassignedModal();
        }
      };
      EventBus.$on("refresh-schedule-board", refreshTimeline);
      applyFilterParam();
      restoreSelection();
    }
  };

  const loadData = (filters: { start_date: Date; end_date: Date }) => {
    // Check if date in store is different than current date before fetching data
    if (store.state.timeline.start_date !== filters.start_date.toISOString()) {
      fetchTimelineData(filters);
      fetchResources();
    } else {
      calendar.value?.navigate(store.state.timeline.start_date);
      calendar.value?.setEvents(events.value);
      setResources(resources.value);
    }
  };

  const setActiveResource = (
    resource: ResourceAPIResponse,
    { replace = false } = {}
  ) => {
    activeResource.value = resource;
    // Pushing the one resource here is the second clamp on the phone board,
    // and the `?resource=` deep link and the flag-off card stack both still
    // route through it. With columns on it has to go through setResources or
    // choosing a schedule would collapse an applied selection to one column.
    if (phoneColumnsEnabled.value) {
      setResources(resources.value);
    } else {
      calendar.value?.setResources([resource]);
    }
    updateQueryParam(
      "resource",
      resource.id === UNASSIGNED_RESOURCE_ID ? null : resource.id.toString(),
      replace
    );
  };

  const setResources = (resources: ResourceAPIResponse[]) => {
    if (layout.value === "mobile") {
      // Taken in the order the `resources` computed built: Unassigned first,
      // then technicians by name. Re-sorting here would fold Unassigned into
      // the alphabetical run and change which schedule the fallback below
      // lands on.
      if (
        !activeResource.value ||
        !resources.find((res) => res.id === activeResource.value?.id)
      ) {
        activeResource.value = resources[0];
      }
      // Pushed every time, not only on re-select: a calendar re-created after a
      // layout swap starts empty while the selection survives in this hook.
      if (phoneColumns.value.length) {
        calendar.value?.setResources(phoneColumns.value);
      }
    } else {
      calendar.value?.setResources(resources);
    }
  };

  const applyTechnicianOffHourHighlights = (
    resources: ResourceAPIResponse[]
  ) => {
    console.log("calculate hours");
    const allColors = resources.flatMap((tech) =>
      [0, 1, 2, 3, 4, 5, 6].flatMap((day) =>
        getUnavailableColorsFromBusinessHours(
          tech.business_hours,
          day,
          tech.id,
          window.currentTenant.timezone
        )
      )
    );

    // Pass into Mobiscroll config
    calendar.value?.setOptions({ colors: allColors });
  };

  const setCalendarDate = async (date: Date) => {
    // Ignore same day
    if (moment(getRefDate()).utc().isSame(moment(date).utc(), "day")) return;
    calendar.value?.navigate(date);
    EventBus.$emit("update-schedule-header-date", date);
    await fetchTimelineData(getDateFilters(date));
  };

  /**
   * Day or week on the phone grid.
   *
   * Applied through setOptions rather than passed at construction: the wrapper
   * merges its own mobile view settings over whatever a caller hands it, so the
   * calendar always starts on day and is moved from here.
   *
   * `maxEventStack` is the difference between a readable week and a coloured
   * smear. At week width a column is 40px, and 'auto' spends 24px of it on a
   * "+N" chip, leaving 9px for the card; 'all' draws every card instead, and
   * MobileTimeline takes the width back in CSS.
   *
   * The caller passes the date to hold, because setOptions re-renders and the
   * grid would otherwise jump off the day being looked at. At construction that
   * is the date the calendar was just built with; `getRefDate` is no use there,
   * since the store has not been filled yet and the calendar's own view date is
   * not readable until it has rendered.
   */
  const applyScheduleView = (refDate: Date | string) => {
    if (!calendar.value || layout.value !== "mobile") return;

    const week = isWeekView.value;
    calendar.value.setOptions({
      refDate,
      // Monday, matching the desktop board's deliberate `firstDay: 1`.
      firstDay: 1,
      view: {
        schedule: {
          type: week ? "week" : "day",
          size: 1,
          maxEventStack: week ? "all" : 4
        }
      }
    });
  };

  // The view and the range it needs move together: the grid would otherwise
  // draw seven columns out of one day's events.
  watch(isWeekView, () => {
    const refDate = getRefDate();
    applyScheduleView(refDate);
    fetchTimelineData(getDateFilters(refDate));
  });

  const getRefDate = () => {
    const viewDate =
      calendar.value?.getViewDate() || store.state.timeline.start_date;
    if (!viewDate) return new Date();
    return viewDate;
  };
  const setCalendarHours = (totalHours: number) => {
    let options = {};
    switch (totalHours) {
      case 6:
        options = { startTime: "09:00", endTime: "15:00" };
        break;
      case 8:
        options = { startTime: "08:00", endTime: "16:00" };
        break;
      case 10:
        options = { startTime: "07:00", endTime: "17:00" };
        break;
      case 14:
        options = { startTime: "05:00", endTime: "19:00" };
        break;
      case 16:
        options = { startTime: "04:00", endTime: "20:00" };
        break;
      case 18:
        options = { startTime: "03:00", endTime: "21:00" };
        break;
      default:
        options = { startTime: "06:00", endTime: "18:00" }; //12hours
    }
    calendar.value?.setOptions({
      refDate: getRefDate(),
      view: {
        timeline: {
          ...calendar.value.desktopViewSettings.timeline,
          ...options
        }
      }
    });
  };
  const refreshTimeline = () => {
    fetchTimelineData(getDateFilters(store.state.timeline.start_date));
  };

  /**
   * Every drag affordance, off while the board is presented as a sheet so a
   * write can only happen through a form.
   *
   * Owned here because both timelines need it: the calendar's own
   * `mobileOptions` do the same job, but only below the `sm` breakpoint
   * (640px), while the sheet gate is `md` (853px). The band between them is
   * ungated in whichever component renders there, and which one that is is a
   * question SchedulePageV2 may yet answer differently.
   *
   * Merged into the options at construction by each timeline, so the initial
   * render already has the right answer.
   */
  const dragOptionsFor = (isSheet: boolean) => {
    const draggable = !isSheet;
    return {
      dragToCreate: draggable,
      dragToMove: draggable,
      dragToResize: draggable,
      dragInTime: draggable,
      dragBetweenResources: draggable,
      externalDrop: draggable
    };
  };

  /**
   * Re-applies the gate after construction, for a placement that changes while
   * the board is open — a resize across the breakpoint, or the tenant flags
   * settling after first paint.
   *
   * `refDate` goes with it for the reason the other setOptions callers pass it:
   * setOptions re-renders the grid, and without it the view can jump off the
   * date the user is looking at.
   */
  const applyDragGate = (isSheet: boolean) => {
    if (!calendar.value) return;
    calendar.value.setOptions({
      refDate: getRefDate(),
      // The phone layout never drags, whatever the sheet gate says. The
      // calendar forces the same six options off for that layout when it is
      // constructed, so a re-apply that disagreed would quietly turn dragging
      // back on there -- with the flag off, where `isSheet` is false all along.
      ...dragOptionsFor(isSheet || layout.value === "mobile")
    });
  };
  const setCalendarZoom = (zoomLevel: 25 | 50 | 75 | 100) => {
    calendar.value?.setOptions({
      refDate: getRefDate(),
      zoomLevel: zoomLevel
    });
    refreshTimeline();
    localStorage.setItem("scheduler-zoom", zoomLevel);
  };
  const reRenderResources = () => {
    setResources(resources.value);
  };
  const startEventPolling = () => {
    stopEventPolling();
    // Update schedule every 2 minutes
    eventPoll.value = setInterval(() => {
      refreshTimeline();
    }, 120_000);
  };
  const stopEventPolling = () => {
    if (eventPoll.value) {
      clearInterval(eventPoll.value);
    }
  };
  const connectToChannel = () => {
    if (!store.state.tenant?.time_cards_enabled) return;
    channel.value = new AdminTimeClockChannel();

    if (channel.value) {
      cable
        .subscribe(channel.value)
        .ensureSubscribed()
        .then(() => {
          channel.value?.on(
            "tenant-clock-in-status-change",
            handleStatusChange
          );
        })
        .catch((err) => {
          console.log("App Unable to join Cable server", err);
        });
    }
  };

  const handleStatusChange = (payload) => {
    const { user_id, action } = payload;
    if (action === "clock_in") {
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).removeClass(
        "bg-red-500"
      );
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).addClass(
        "bg-green-500"
      );
    } else {
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).removeClass(
        "bg-green-500"
      );
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).addClass(
        "bg-red-500"
      );
    }
  };
  const setTimezoneLabel = () => {
    setTimeout(() => {
      const timeIndicator = document.querySelector(
        ".mbsc-schedule-time-indicator-time"
      );
      if (
        timeIndicator &&
        !timeIndicator.contains(document.querySelector(".timezone-label"))
      ) {
        const timezone = document.createElement("span");
        timezone.classList.add("timezone-label");
        timezone.innerHTML = ` (${window.currentTenant.timezone_abbreviation})`;
        timeIndicator.appendChild(timezone);
      }
    });
  };
  const handleEventRightClick = (args: MbscEventClickEvent) => {
    args.domEvent.preventDefault();
    const event = args.event.details;
    if (!event?.appointment) return;
    // A copy's appointment.status is its technician's own state, but Cancel
    // acts on the real appointment.
    const appointmentStatus = event.rollup_status ?? event.appointment.status;
    const content = document.createElement("div");
    content.innerHTML = `
      <div class="bg-white shadow rounded-lg border p-2 *:cursor-pointer *:p-2 *:rounded-lg">
        <div class="hover:bg-blue-50" onclick="handleEdit(${
          event.appointment.id
        })">Edit</div>
        <div class="hover:bg-blue-50 ${
          appointmentStatus !== "pending" ? "text-gray-400" : ""
        }" onclick="${
          appointmentStatus === "pending"
            ? `handleEdit(${event.appointment.id})`
            : ""
        }">Cancel</div>
        <a class="hover:bg-blue-50 block no-underline text-gray-900" href="${withStartParam(
          jobShowPath(event.appointment.job_id, event.appointment.id)
        )}">View Appointment</a>
        <a class="hover:bg-blue-50 block no-underline text-gray-900" href="${userShowPath(
          event.appointment.customer.id
        )}">View Customer</a>
      </div>
      `;
    document.body.appendChild(content);

    // options with default values
    const options: PopoverOptions = {
      placement: "bottom-end",
      triggerType: "click",
      onHide: () => {
        popover.destroy();
      }
    };
    const popover: PopoverInterface = new Popover(
      content,
      args.domEvent.target,
      options
    );

    popover.show();
  };

  // Tracked here rather than in either timeline: this is the single point both
  // the phone and the desktop board route a card tap through, so the event is
  // counted once and carries the same shape for either surface. trackMobileWebEvent
  // stamps the layout, which is what makes "taps on phone widths" answerable.
  const handleEventClick = (args: MbscEventClickEvent) => {
    const event = args.event.details;
    if (!event?.appointment) {
      trackMobileWebEvent("schedule_card_tap", {
        board: "v2",
        target: "calendar_event"
      });
      return (selectedEventId.value = args.event.id);
    }
    trackMobileWebEvent("schedule_card_tap", {
      board: "v2",
      target: "appointment",
      appointment_id: event.appointment.id
    });
    selectedAppointmentId.value = event.appointment.id;
  };

  /**
   * The technician a tapped cell belongs to, as the create form wants it.
   *
   * Mobiscroll resources are built with `id: tech.id`, which the jbuilder sets
   * to the technician PROFILE id, while the form's technician options are keyed
   * on `user_id` — so handing the resource id straight to the form would either
   * select the wrong technician or none.
   *
   * Everything without a technician behind it falls out of the same lookup miss:
   * the -1 "Unassigned" pseudo-row lives only in the local `resources` computed
   * and never in the store, a department group row carries a string id no
   * technician row can match, and an id dropped from the list matches nothing.
   * Ids compare as strings because those group rows put a string in the field
   * technician rows put a number in.
   */
  const technicianUserIdForResource = (
    resourceId: number | string | undefined
  ) => {
    const match = store.state.timeline.resources.find(
      (resource: ResourceAPIResponse) =>
        String(resource.id) === String(resourceId)
    );
    return match?.user_id ?? undefined;
  };

  /**
   * The instant a tapped cell stands for, in the tenant's timezone.
   *
   * Mobiscroll does not hand back a usable instant. With the timezone plugins
   * configured it builds a Date whose BROWSER-LOCAL getters read back as the
   * wall clock it painted — tap the 8 AM row on the 20th and `getHours()` is 8
   * and `getDate()` is 20, whatever zone either side is in. Passing that Date to
   * a tenant-pinned `moment()` reinterprets the instant instead of the reading,
   * so a browser east of the tenant lands on the previous day: the tap is then
   * swallowed by the past-day guard, or books a day early.
   *
   * So every field is taken from the cell's own reading and rebuilt in the
   * tenant's zone. The board's date is deliberately NOT consulted: `getRefDate`
   * falls back to `store.state.timeline.start_date`, which is only committed
   * when a fetch lands, so between navigating and that response it still holds
   * the previous day.
   */
  const slotFromCell = (cellDate: MbscCellClickEvent["date"]) => {
    const painted = cellDate instanceof Date ? cellDate : new Date(cellDate);
    if (Number.isNaN(painted.getTime())) return null;

    return moment([
      painted.getFullYear(),
      painted.getMonth(),
      painted.getDate(),
      painted.getHours(),
      painted.getMinutes()
    ]);
  };

  /**
   * A tapped empty cell, held until the create form has been built from it.
   *
   * Mirrors selectedAppointmentId / selectedEventId: the composable records
   * what was tapped, and the timeline decides whether to present anything.
   */
  const createSlot = ref<{
    date: Date;
    // The tapped time as a tenant-offset ISO string. The availability picker
    // reads it with `moment.parseZone` and preselects the slot on that hour,
    // falling back to the nearest one, so the offset has to survive the trip —
    // a bare Date would be re-read in the browser's zone and select the wrong
    // hour for any tenant the dispatcher is not sitting in.
    scheduledTime: string;
    technicianUserId?: number;
  } | null>(null);

  const handleCellClick = (args: MbscCellClickEvent) => {
    // A create form already built from an earlier tap. Its backdrop covers the
    // grid, so in a browser this is unreachable — but useAppointmentSchedulingFields
    // is a provideHook, and a second form in the same tree would silently inherit
    // the first one's formData, so the cheap guard is worth having.
    if (createSlot.value) return;

    const slot = slotFromCell(args.date);

    // A past day is dropped rather than carried: the availability picker only
    // searches today onward, so passing one would search a day it cannot book.
    // Same rule CreateEventButton applies to the board date.
    if (!slot || slot.isBefore(moment(), "day")) return;

    createSlot.value = {
      date: slot.toDate(),
      scheduledTime: slot.format(),
      technicianUserId: technicianUserIdForResource(args.resource)
    };
    trackMobileWebEvent("create_sheet_open", { source: "empty_slot" });
  };

  const clearCreateSlot = () => {
    createSlot.value = null;
  };

  const applyFilterParam = () => {
    const params = getQueryVariables();
    const filters: {
      departmentIds?: number[];
      serviceCategoryIds?: number[];
      statuses?: string[];
      jobStatusIds?: string[];
    } = {};
    if (params.departmentIds) {
      filters.departmentIds = params.departmentIds
        .split(",")
        .map((id) => parseInt(id, 10));
    }
    if (params.serviceCategoryIds) {
      filters.serviceCategoryIds = params.serviceCategoryIds
        .split(",")
        .map((id) => parseInt(id, 10));
    }
    if (params.statuses) {
      filters.statuses = params.statuses.split(",");
    }
    if (params.jobStatusIds) {
      filters.jobStatusIds = params.jobStatusIds.split(",");
    }
    store.dispatch("setTimelineFilters", filters);
  };

  return {
    activeResource,
    calendar,
    events,
    isWeekView,
    phoneColumns,
    phoneColumnsEnabled,
    resources,
    selectedResources,
    unassignedEvents,
    selectedAppointmentId,
    selectedEventId,
    createSlot,

    applyDragGate,
    clearCreateSlot,
    dragOptionsFor,
    handleCellClick,
    destroyCalendar,
    initCalendar,
    setActiveResource,
    setSelectedResources,
    setCalendarDate,
    setCalendarHours,
    setCalendarZoom,
    refreshTimeline,
    fetchResources,
    reRenderResources,
    setTimezoneLabel,
    handleEventRightClick,
    handleEventClick
  };
});
